import { User } from '@sendbird/chat';
import { GroupChannel } from '@sendbird/chat/groupChannel';
import { Role } from '@app/lib/roles.const';
import { CONNECTION_STATE } from './messaging.constants';
import { IMessage as IMessageSDK } from '@app/dashboard/messaging/sendbird-common-types';

export enum CHANNEL_TYPE {
  GROUP = 'groupMessage',
  DIRECT = 'directMessage',
  TEAM = 'team', // Channel customType for teams is team_{{id}}
  BROADCAST = 'text/broadcast',
}

export enum MESSAGE_CUSTOM_TYPE {
  TEXT = 'text',
  BROADCAST = 'text/broadcast',
  PDF = 'pdf',
  IMAGE = 'image',
}

export enum MESSAGE_SENDING_STATUS {
  NONE = 'none',
  PENDING = 'pending',
  FAILED ='failed',
  CANCELED = 'canceled',
  SUCCEEDED = 'succeeded',
}

export type IMessage = IMessageSDK;

export type ILocationViewType = 'mini' | '';

export interface IMessageLocation {
  location: string;
  lat: number;
  lon: number;
}

export interface IBroadcastRecipientResponse {
  recipients: IBroadcastRecipient[];
  per_page: number;
  page_no: number;
  total: number;
}

export interface IBroadcastRecipientUser {
  id: string;
  first_name: string;
  last_name: string;
  role: string;
  deactivated_at: string;
}

export interface IBroadcastRecipient {
  recipient: IBroadcastRecipientUser;
}

export interface IBroadcastLastMessage {
  id: number;
  body: string;
  created_at: string;
  file_attachment?: IBroadcastFileAttachmentResponse;
}

export interface IBroadcastRequestPayload {
  id: number;
  name: string;
  last_message: IBroadcastLastMessage;
  recipients?: IBroadcastRecipient[];
  recipients_count?: number;
}

export interface IBroadcastRequestBody {
  recipient_ids?: number[];
  body: string;
  name?: string;
  file_attachment_id?: number;
  id?: string;
}

export interface IBroadcastResponse {
  broadcast: IBroadcastRequestPayload;
}

export interface IBroadcasts {
  broadcasts: IBroadcastResponse[];
  per_page: number,
  page_no: number,
  total: number
}

export interface IMotiveUserResponse {
  deactivated_at: string;
  driver_company_id: string;
  email: string;
  first_name: string;
  id: string;
  last_name: string;
  role: string;
  name?: string;
  user_roles?: IUserRole[];
}

export interface IUserRole {
  id: number;
  name: string;
}

export interface IParticipant {
  id: string;
  displayName?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  initials?: string;
  role: Role | string; // Some response interfaces have type string but correspond to Role enum
}

export interface IParticipantMetadataResponse {
  company_id: string;
  first_name: string;
  last_name: string;
  role: Role;
}

export interface IConnectionState {
  state: CONNECTION_STATE;
  numberOfTabsInSW?: number;
  type?: 'connecting' | 'initialConnect' | 'alreadyConnected' | 'reconnect' | 'retry' | 'failed';
}

export interface ITypingIndicatorEvent {
  users: User[];
  channel: GroupChannel;
}

// File attachments
export interface ICreateFileRequestBody {
  file_attachments: IFileAttachmentParams[];
}

export interface IFileAttachmentParams {
  file_name: string;
  file_type: string;
  file_size: number;
  offline_id: string; // For mobile caching
  thumbnail?: IThumbnailFileAttachmentParams;
}

export interface IThumbnailFileAttachmentParams {
  offline_id: string;
}

export interface IThumbnailFileAttachmentResponse {
  offline_id: string;
  upload_url: string;
}

export interface ICreateFileResponse {
  file_attachments: IFileAttachmentResponse[];
}

export interface IFileAttachmentResponse {
  file_attachment: IFileAttachment
}

export interface IFileAttachment {
  id: string;
  file_name: string;
  file_type: string;
  file_size: number;
  offline_id: string;
  upload_url: string;
  thumbnail?: IThumbnailFileAttachmentResponse;
}

export interface ICreateFileResponse {
  file_attachments: IFileAttachmentResponse[];
}

export interface IBroadcastFileAttachmentResponse {
  id: string;
  file_name: string;
  file_type: string;
  file_size: number;
  offline_id: string;
  public_url: string;
  thumbnail?: IBroadcastThumbnailFileAttachmentResponse;
}

export interface IBroadcastThumbnailFileAttachmentResponse {
  offline_id: string;
  public_url: string;
}

export interface IFileAttachmentsFetchResponse {
  file_attachments: IFileAttachmentFetch[];
}

export interface IFileAttachmentFetch {
  file_attachment: IFileAttachmentFetchData;
}

export interface IFileAttachmentFetchData {
  id: string;
  file_name: string;
  file_size: number;
  file_type: string;
  offline_id: string;
  public_url: string;
  thumbnail?: {
    offline_id: string;
    public_url: string;
  }
}

export interface IFileAttachmentDownloadUrls {
  mainFileUrl: string;
  thumbnailUrl?: string;
}
